import { ApiError } from './envelope';

/**
 * Turn a refusal into the field that caused it.
 *
 * **A rejection belongs on the control the operator can act on.** Every service
 * here answers a validation failure with `details[]`, each entry naming a
 * `field`, a `code` and a message — and the console threw all of it away and drew
 * one page-level line saying «review the marked fields», with nothing marked.
 *
 * The cost of that is asymmetric and it lands on the longest action in the
 * product. An authoring form carries a file transfer measured in minutes beside
 * fields that validate in milliseconds; a whole-form rejection discards work that
 * was already complete and correct, and the operator re-does the upload to fix a
 * year.
 *
 * `body.` is stripped because it is the *location* of the field, not its name:
 * the service reports `body.contentType` and the control is called `contentType`.
 * Both spellings are indexed for the same reason the services now accept both — a
 * surface should not have to know which one the answer happened to use.
 */
export function fieldErrorsFrom(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || !error.details) return {};

  const mapped: Record<string, string> = {};
  for (const detail of error.details) {
    if (!detail.field) continue;
    const name = detail.field.replace(/^body\./, '').replace(/^query\./, '');
    const message = detail.message ?? detail.code ?? '';
    if (!message) continue;
    mapped[name] = message;
    mapped[toCamel(name)] = message;
    mapped[toSnake(name)] = message;
  }
  return mapped;
}

/**
 * Whether a refusal named any field at all.
 *
 * The page-level line is for a failure that belongs to no single control — a
 * conflict, an upstream timeout, a permission. Drawing it *as well* beside a
 * marked field says the same thing twice and buries which of the two the
 * operator should read.
 */
export function hasFieldErrors(error: unknown): boolean {
  return Object.keys(fieldErrorsFrom(error)).length > 0;
}

function toCamel(name: string): string {
  return name.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());
}

function toSnake(name: string): string {
  return name.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
